/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import Reset from '@carbon/web-components/es/icons/reset/16.js';
import HostListenerMixin from '@carbon/web-components/es/globals/mixins/host-listener.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './filter-panel.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * The filter panel.
 *
 * @element c4d-filter-panel
 * @csspart section - The filter panel section. Usage: `c4d-filter-panel::part(section)`
 * @csspart heading-clear - Contains the filter heading an the button. Usage: `c4d-filter-panel::part(heading-clear)`
 * @csspart filter-heading - The heading. Usage: `c4d-filter-panel::part(filter-heading)`
 * @csspart button-clear - The button. Usage: `c4d-filter-panel::part(button-clear)`
 * @csspart container - The button inner container. Usage: `c4d-filter-panel::part(container)`
 * @csspart icon - The button icon. Usage: `c4d-filter-panel::part(icon)`
 */
@customElement(`${c4dPrefix}-filter-panel`)
class C4DFilterPanel extends HostListenerMixin(
  StableSelectorMixin(LitElement)
) {
  /**
   * Renders the filter heading
   */
  @property()
  heading!: string;

  /**
   * Handles `click` event on the `<input>` in the shadow DOM.
   */
  protected _handleClear() {
    const { eventSelectionClear } = this.constructor as typeof C4DFilterPanel;
    this.dispatchEvent(
      new CustomEvent(eventSelectionClear, {
        bubbles: true,
        composed: true,
        detail: {
          clear: true,
        },
      })
    );
  }

  /**
   * the filter title
   */
  @property()
  title = '';

  /**
   * renders the selected values
   */
  @property()
  selectedValues: any[] = [];

  /**
   * Handles items in the selected array
   */
  @property({ attribute: 'has-selections', type: Boolean })
  hasSelections = false;

  render() {
    return html`
      <section class="${prefix}--filter-panel__section" part="section">
        <div class="${prefix}--heading-clear" part="heading-clear">
          <div class="${prefix}--filter__heading" part="filter-heading">
            ${this.heading}
          </div>
          <button
            class="${prefix}--clear"
            part="button-clear"
            @click=${this._handleClear}
            ?disabled="${!this.hasSelections}">
            <div class="${prefix}--clear__container" part="container">
              Clear
              <div class="${prefix}--reset__icon" part="icon">${Reset()}</div>
            </div>
          </button>
        </div>
        <slot></slot>
      </section>
    `;
  }

  /**
   * The name of the custom event fired to clear selections
   */

  static get eventSelectionClear() {
    return `${c4dPrefix}-selection-clear`;
  }

  static get stableSelector() {
    return `${c4dPrefix}-filter-panel`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DFilterPanel;
